/**
 * useFeatureAccess
 *
 * Single source of truth for "can the current user reach feature X?"
 * across the app. Reads the user's `feature:<key>` permission so the
 * /settings/permissions Feature Access matrix is actually
 * load-bearing — toggling a feature off for a role removes the
 * permission row, and `useFeatureAccess` returns false.
 *
 * Bootstrap fallback: until an admin has visited the Feature Access
 * tab (which seeds the `feature:*` permission rows + role grants), a
 * user's `permissions` list will contain zero `feature:*` entries.
 * In that pre-seed state we fall back to the catalog's `defaultTier`
 * matched against the user's role name — i.e., the same tier logic
 * the sidebar already encodes. This way the app keeps working on a
 * fresh install before the seeder has ever run; once any toggle has
 * fired, the matrix takes over fully.
 *
 * No admin short-circuit: this hook reads `permissions` directly and
 * deliberately does NOT route through `auth.store.hasPermission`,
 * which short-circuits to `true` for `user.isAdmin === true`. That
 * short-circuit is correct for generic perms (`users:edit`, etc.) but
 * wrong for `feature:*` gates, where the matrix has to be honored
 * even for admins — otherwise an admin can't preview what toggling a
 * feature off does to anyone, including themselves. Recovery if an
 * admin locks themselves out of `settings_permissions`: a Super Admin
 * (skipsPermissionChecks) can always toggle it back on.
 */
import { useAuth } from '../contexts/AuthContext';
import {
  EXACT_GRANT_ONLY_FEATURES,
  FEATURE_CATALOG,
  featureKeyToPermName,
  isFeaturePermission,
  roleHasDefaultAccessToTier,
  skipsPermissionChecks,
} from '@aiga/app-config/permissions';
import { isAigStaff } from '@aiga/app-config/security';

/**
 * Keys with NO admin shortcut and NO tier fallback: true only when the user holds the exact
 * permission. The server enforces the same rule (a Super Admin without the key gets 403). The set
 * lives in @aiga/app-config/permissions next to the catalog (CF-4).
 */
export { EXACT_GRANT_ONLY_FEATURES };

export function useFeatureAccess(featureKey: string): boolean {
  const { permissions, role, user } = useAuth();
  const permName = featureKeyToPermName(featureKey);
  const perms = permissions ?? [];

  // AIG staff (whitelisted email AND an admin role) hold every key, the exact-grant-only
  // ones included; the server applies the same rule (docs/SECURITY.md "AIG staff hold every key").
  if (isAigStaff({ email: user?.email, isAdmin: user?.isAdmin ?? false })) return true;

  if (EXACT_GRANT_ONLY_FEATURES.has(featureKey)) return perms.some((p) => p.name === permName);

  // Super Admin always sees every feature (skipsPermissionChecks, the
  // API's rule too). The matrix can still be used to preview gating for
  // OTHER roles from an admin account, but a Super Admin can never lock
  // themselves out — this is the recovery role. Admin follows its grants.
  if (skipsPermissionChecks({ role: role?.name, email: user?.email, isAdmin: user?.isAdmin })) return true;

  // Direct check against the user's permissions array. We deliberately
  // do NOT delegate to `hasPermission(permName)` here because that
  // helper short-circuits on `user.isAdmin === true` — fine for
  // generic perms like `users:edit`, but wrong for feature gates,
  // where the Feature Access matrix MUST be authoritative. Without
  // this carve-out, an admin who toggles `feature:settings_submarkets`
  // off for their own role would still see the card, and there would
  // be no way to preview what the matrix does to other users from an
  // admin account.
  const hasFeaturePerm = perms.some((p) => p.name === permName);
  if (hasFeaturePerm) return true;

  // If any `feature:*` permission has been seeded for this user at
  // all, the feature system is the authority — a miss means denied,
  // regardless of role tier.
  const userHasAnyFeaturePerm = perms.some((p) =>
    isFeaturePermission(p.name)
  );
  if (userHasAnyFeaturePerm) return false;

  // Bootstrap fallback: the seeder hasn't run yet for this user's
  // role. Use the catalog default tier so a brand-new install or a
  // role that's never visited the matrix doesn't render an empty
  // sidebar. Admins still get every `admin+` feature via this path
  // pre-seed; once any toggle is saved, the matrix takes over.
  const feature = FEATURE_CATALOG.find((f) => f.key === featureKey);
  if (!feature) return true;
  return roleHasDefaultAccessToTier(role?.name ?? '', feature.defaultTier);
}
